import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const LeadTime = () => {
  // State variables for Lead Time calculation
  const [supplyDelay, setSupplyDelay] = useState(0); // Supply delay in days
  const [reorderingDelay, setReorderingDelay] = useState(0); // Reordering delay in days
  const [calculatedLeadTime, setCalculatedLeadTime] = useState(0); // Calculated lead time in days

  // Event handlers to update state values
  const handleSupplyDelayChange = (e) => setSupplyDelay(parseFloat(e.target.value) || 0);
  const handleReorderingDelayChange = (e) => setReorderingDelay(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Lead Time
  useEffect(() => {
    // Lead Time = Supply Delay + Reordering Delay
    const totalLeadTime = supplyDelay + reorderingDelay;
    setCalculatedLeadTime(totalLeadTime);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [supplyDelay, reorderingDelay]);

  return (
    <Layout title="Lead Time Calculator" path="lead-time">
      <section className="section-padding" id="lead-time-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Lead Time Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Lead Time Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Supply Delay */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="supplyDelay" className="form-label">
                        Supply Delay (Days)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Supply Delay:</strong> The number of days it takes for the supplier to deliver the required items."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="supplyDelay"
                        placeholder="Enter supply delay"
                        value={supplyDelay || ""}
                        onChange={handleSupplyDelayChange}
                      />
                    </div>

                    {/* Input for Reordering Delay */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="reorderingDelay" className="form-label">
                        Reordering Delay (Days)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Reordering Delay:</strong> The number of days it takes to reorder and replenish stock after the order is placed."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="reorderingDelay"
                        placeholder="Enter reordering delay"
                        value={reorderingDelay || ""}
                        onChange={handleReorderingDelayChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Calculated Lead Time:{" "}
                        <span className="fw-bold ms-3">
                          {calculatedLeadTime === 0 ? "0" : calculatedLeadTime} days
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Lead Time Calculator
                </h3>

                {/* What is a Lead Time Calculator? */}
                <h4 className="mt-3 mb-3">What is a Lead Time Calculator?</h4>
                <p>
                  The **Lead Time Calculator** helps calculate the total time required from placing an order to receiving the goods. This is critical for efficient supply chain management.
                </p>
                <ul>
                  <li><strong>Supply Delay:</strong> The time it takes for the supplier to fulfill the order.</li>
                  <li><strong>Reordering Delay:</strong> The time it takes to reorder stock once inventory levels are low.</li>
                </ul>

                {/* Why Use a Lead Time Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Lead Time Calculator?</h4>
                <p>
                  This tool helps businesses manage their inventory and plan for timely reordering. By calculating the total lead time, companies can make better decisions on when to place orders, avoiding stockouts and production delays.
                </p>

                {/* Benefits of Using a Lead Time Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Lead Time Calculator</h4>
                <ul>
                  <li><strong>Helps Manage Inventory and Supply Chain Planning:</strong> By knowing the total lead time, businesses can more effectively plan their inventory levels and reordering schedules.</li>
                  <li><strong>Reduces Stockout Risks and Production Delays:</strong> Accurate lead time estimations help avoid situations where production slows down due to lack of materials.</li>
                  <li><strong>Supports Efficient Scheduling:</strong> By calculating lead times, companies can align their production and order schedules more efficiently.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Lead Time Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Manufacturing:</strong> Helps manufacturing companies calculate and manage their supply chain lead times.</li>
                  <li><strong>Retail:</strong> Supports retail businesses by determining how long it will take to receive inventory.</li>
                  <li><strong>Logistics:</strong> Assists logistics teams in managing delivery schedules and ensuring timely arrivals.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LeadTime;
